import { useCallback, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { NotesTotalsByDay } from 'modules/notes/hooks';
import { TimeSignal } from 'graphql/types';

type ReturnType = {
  selectAll: boolean;
  indeterminate: boolean;
  selectedCount: number;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  clearSelection: () => void;
  selectAllNotes: () => void;
};

export const useNotesHeaderCheckActions = ({
  totalCount,
  notes
}: {
  totalCount: number;
  notes: TimeSignal[];
}): ReturnType => {
  const { control, setValue } = useFormContext();
  const values = useWatch({ control } as any);
  const selectedCount = useMemo(
    () => notes.filter((note) => values[`${note.date}_${note.id}`]).length,
    [notes, values]
  );

  const valueKeys = Object.keys(values);
  const filteredValues = valueKeys.filter((key) => {
    const keySplit = key.split('_');
    return keySplit.length > 1 && notes.some(({ id }) => id === keySplit[1]);
  });

  const { selectAll, indeterminate } = useMemo(() => {
    return {
      selectAll: totalCount > 0 && selectedCount === totalCount,
      indeterminate: selectedCount > 0 && selectedCount < totalCount
    };
  }, [totalCount, selectedCount]);

  const onChange = useCallback(() => {
    setValue('selectAll', selectAll, {
      shouldDirty: true
    });
    filteredValues.forEach((id) =>
      setValue(id, !(selectAll || indeterminate), {
        shouldDirty: true
      })
    );
  }, [setValue, filteredValues, selectAll, indeterminate]);

  const clearSelection = useCallback(() => {
    filteredValues.forEach((id) =>
      setValue(id, false, {
        shouldDirty: false
      })
    );
    setValue('selectAll', false, { shouldDirty: false });
  }, [filteredValues, setValue]);

  const selectAllNotes = useCallback(() => {
    filteredValues.forEach((id) =>
      setValue(id, true, {
        shouldDirty: true
      })
    );
    setValue('selectAll', true, { shouldDirty: true });
  }, [filteredValues, setValue]);

  return {
    selectAll,
    indeterminate,
    selectedCount,
    onChange,
    clearSelection,
    selectAllNotes
  };
};

export const useKeysByDay = ({
  summaryByDay = {},
  selections
}: {
  summaryByDay: NotesTotalsByDay;
  selections: Record<string, boolean | undefined>;
}): Record<string, string[]> => {
  const daysWithNotes = useMemo(
    () => Object.keys(summaryByDay).filter((key) => summaryByDay[key].count),
    [summaryByDay]
  );

  return useMemo(() => {
    const daysMap: Record<string, string[]> = daysWithNotes.reduce(
      (acc, day) => {
        acc[day] = [];
        return acc;
      },
      {} as Record<string, string[]>
    );

    return Object.keys(selections).reduce((acc, key) => {
      const keySplit = key.split('_');
      if (keySplit.length > 1) {
        acc[keySplit[0]]?.push(key);
      }
      return acc;
    }, daysMap);
  }, [daysWithNotes, selections]);
};
